/**
 * OPS-STAB-003 — shared keyboard-first execution framework (both shells).
 */
import { isTypingTarget } from './keyboardStandards.js';

/** Canonical function keys — same meaning in frontend + healthcare-ui. */
export const EXECUTION_KEYS = {
  SEARCH_PATIENT: 'F2',
  PAYMENT: 'F4',
  PRINT: 'F6',
  REFRESH_QUEUE: 'F8',
  FOCUS_SCAN: 'F3',
  VERIFY: 'Enter',
  CONFIRM: 'Enter',
  NEXT_ACTION: 'Enter',
  ESCAPE: 'Escape',
  QUEUE_DOWN: 'ArrowDown',
  QUEUE_UP: 'ArrowUp',
  QUICK_SEARCH: '/',
  REFRESH_LETTER: 'r',
  SEARCH_MOD: 'k',
};

export const MOD = {
  CTRL_ENTER: { ctrl: true, key: 'Enter' },
  CTRL_K: { ctrl: true, key: 'k' },
  ALT_R: { alt: true, key: 'r' },
};

/**
 * Per-workflow default bindings (handlers supplied by each screen).
 * @typedef {object} KeyboardBindingHandlers
 */

export const WORKFLOW_KEYBOARD = {
  BILLING: {
    keys: {
      [EXECUTION_KEYS.SEARCH_PATIENT]: 'onSearchFocus',
      [EXECUTION_KEYS.PAYMENT]: 'onPaymentFocus',
      [EXECUTION_KEYS.PRINT]: 'onPrint',
      [EXECUTION_KEYS.REFRESH_QUEUE]: 'onRefreshQueue',
      [EXECUTION_KEYS.ESCAPE]: 'onEscape',
      [EXECUTION_KEYS.QUICK_SEARCH]: 'onSearchFocus',
    },
    mods: {
      'ctrl+Enter': 'onConfirmPayment',
      'ctrl+k': 'onSearchFocus',
      'alt+r': 'onRefundReview',
    },
  },
  PHARMACY: {
    keys: {
      [EXECUTION_KEYS.FOCUS_SCAN]: 'onScanFocus',
      [EXECUTION_KEYS.PRINT]: 'onPrint',
      [EXECUTION_KEYS.REFRESH_QUEUE]: 'onRefreshQueue',
      [EXECUTION_KEYS.PAYMENT]: 'onFulfill',
      [EXECUTION_KEYS.ESCAPE]: 'onEscape',
    },
    mods: {
      'ctrl+Enter': 'onFulfill',
    },
  },
  LAB: {
    keys: {
      [EXECUTION_KEYS.SEARCH_PATIENT]: 'onAccessionSearch',
      [EXECUTION_KEYS.PRINT]: 'onPrint',
      [EXECUTION_KEYS.REFRESH_QUEUE]: 'onRefreshQueue',
      [EXECUTION_KEYS.VERIFY]: 'onVerify',
      [EXECUTION_KEYS.ESCAPE]: 'onEscape',
    },
    mods: {
      'ctrl+Enter': 'onVerify',
    },
  },
  RADIOLOGY: {
    keys: {
      [EXECUTION_KEYS.SEARCH_PATIENT]: 'onReportSearch',
      [EXECUTION_KEYS.PRINT]: 'onPrint',
      [EXECUTION_KEYS.REFRESH_QUEUE]: 'onRefreshQueue',
      [EXECUTION_KEYS.VERIFY]: 'onVerify',
      [EXECUTION_KEYS.ESCAPE]: 'onEscape',
    },
    mods: {
      'ctrl+Enter': 'onVerify',
    },
  },
  DISCHARGE: {
    keys: {
      [EXECUTION_KEYS.PAYMENT]: 'onSettlement',
      [EXECUTION_KEYS.PRINT]: 'onPrint',
      [EXECUTION_KEYS.REFRESH_QUEUE]: 'onRefreshQueue',
      [EXECUTION_KEYS.ESCAPE]: 'onEscape',
    },
    mods: {
      'ctrl+Enter': 'onConfirmDischarge',
    },
  },
  ALERTS: {
    keys: {
      [EXECUTION_KEYS.REFRESH_QUEUE]: 'onRefreshQueue',
      [EXECUTION_KEYS.ESCAPE]: 'onClosePanel',
      [EXECUTION_KEYS.QUICK_SEARCH]: 'onSearchFocus',
    },
    mods: {},
  },
};

function normKey(key) {
  return String(key ?? '').toLowerCase();
}

function matchMod(e, spec) {
  const key = normKey(spec.key);
  const eKey = normKey(e.key);
  if (eKey !== key) return false;
  const needCtrl = Boolean(spec.ctrl);
  const needAlt = Boolean(spec.alt);
  const needMeta = Boolean(spec.meta);
  const ctrl = e.ctrlKey || e.metaKey;
  if (needCtrl && !ctrl) return false;
  if (needAlt && !e.altKey) return false;
  if (needMeta && !e.metaKey) return false;
  if (!needCtrl && !needAlt && (e.ctrlKey || e.altKey || e.metaKey) && key.length > 1) {
    return false;
  }
  return true;
}

function modKey(e) {
  const parts = [];
  if (e.ctrlKey || e.metaKey) parts.push('ctrl');
  if (e.altKey) parts.push('alt');
  parts.push(normKey(e.key));
  return parts.join('+');
}

/**
 * Dispatch keyboard event to handler map.
 * @param {KeyboardEvent} e
 * @param {Record<string, Function|undefined>} handlers
 * @param {{ enabled?: boolean, allowWhenTyping?: string[] }} [options]
 */
export function dispatchOperationalKeyboard(e, handlers, options = {}) {
  if (options.enabled === false) return false;
  const allowWhenTyping = options.allowWhenTyping ?? [EXECUTION_KEYS.ESCAPE, EXECUTION_KEYS.VERIFY];
  const typing = isTypingTarget(e.target);
  if (typing && !allowWhenTyping.includes(e.key)) {
    if (e.key === EXECUTION_KEYS.ESCAPE) {
      e.preventDefault();
      handlers.onEscape?.();
      e.target?.blur?.();
      return true;
    }
    return false;
  }

  if (!typing && e.key === EXECUTION_KEYS.REFRESH_LETTER && !e.ctrlKey && !e.altKey && !e.metaKey) {
    const fn = handlers.onRefreshQueue;
    if (fn) {
      e.preventDefault();
      fn();
      return true;
    }
  }

  const mod = modKey(e);
  const modFn = handlers[`mod:${mod}`];
  if (modFn) {
    e.preventDefault();
    modFn();
    return true;
  }

  const fn = handlers[`key:${e.key}`] ?? handlers[e.key];
  if (fn) {
    e.preventDefault();
    fn();
    return true;
  }

  return false;
}

/**
 * Build handler map from workflow template + concrete callbacks.
 */
export function buildKeyboardHandlers(workflowId, callbacks = {}) {
  const template = WORKFLOW_KEYBOARD[workflowId];
  const handlers = { ...callbacks };
  if (!template) return handlers;
  for (const [key, name] of Object.entries(template.keys ?? {})) {
    if (typeof callbacks[name] === 'function') handlers[`key:${key}`] = callbacks[name];
  }
  for (const [mod, name] of Object.entries(template.mods ?? {})) {
    if (typeof callbacks[name] === 'function') handlers[`mod:${mod}`] = callbacks[name];
  }
  return handlers;
}

/**
 * Attach global keydown listener (for non-React consumers / tests).
 */
export function attachOperationalKeyboard(target, handlers, options = {}) {
  const listener = (e) => dispatchOperationalKeyboard(e, handlers, options);
  target.addEventListener('keydown', listener);
  return () => target.removeEventListener('keydown', listener);
}

export function keyboardHintLine(workflowId) {
  const t = WORKFLOW_KEYBOARD[workflowId];
  if (!t) return '';
  const keys = Object.keys(t.keys ?? {});
  return keys.slice(0, 5).join(' · ');
}
